import React, { CSSProperties } from "react"
import autobind from "class-autobind"
import { TextField, TimePicker } from "material-ui"
import { ISimpleTime } from "../../models"
import * as timeHelper from "../../helpers/timeHelper"
import _ from "lodash"
import { isMobile } from "react-device-detect"
import moment from "moment"
import MobileClickableTextField from "./MobileClickableTextField"

interface IProps {
  label?: string
  simpleTime?: ISimpleTime
  readonly?: boolean
  textFieldStyle?: CSSProperties
  semicolonColor?: string
  handleChange?(simpleTime: ISimpleTime): void
  // we don't want immediately updating when change the time of punchset on timecard
  // in this case, should set handleSlowChange method
  handleSlowChange?(simpleTime: ISimpleTime, revertToOldValueCb: () => void): void
  autoFocus?: boolean

  dataIntercomTargetStr?: string

  //small in timecard
  smallText?: boolean

  //we should show punch detail dialog when click editor in timecard page
  handleMobileClick?(): void

  // if timeoff punchset, not allow to update time
  checkAllowChange?(): boolean
}
interface IState {
  hour: string
  minute: string
  ampm: string
  hourBuffer: string[]
  minuteBuffer: string[]
  dirty: boolean
}

export default class SimpleTimeEditor extends React.Component<IProps, IState> {
  private saveTimeout
  private hourField
  private minuteField
  private ampmField
  constructor(props) {
    super(props)
    this.state = {
      hour: "00",
      minute: "00",
      ampm: "am",
      hourBuffer: [null, null],
      minuteBuffer: [null, null],
      dirty: false
    }
    autobind(this)
  }

  componentDidMount() {
    if (!this.props.simpleTime) return
    this.getTimeFromSimpleTime(this.props.simpleTime)
    this.props.autoFocus &&
      this.hourField.focus()
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.simpleTime || _.isEqual(this.props.simpleTime, nextProps.simpleTime) || _.isEqual(timeHelper.getSimpleTimeFromHourMinuteAm(this.state.hour, this.state.minute, this.state.ampm), nextProps.simpleTime)) return
    this.getTimeFromSimpleTime(nextProps.simpleTime)
  }

  componentWillUnmount() {
    this.saveTimeout && clearTimeout(this.saveTimeout)
  }

  getTimeFromSimpleTime(simpleTime: ISimpleTime) {
    const timeFormat = timeHelper.getHourMinuteAmFromSimpleTime(simpleTime);
    ((timeFormat.hour === "12" || timeFormat.hour === "00") &&
      (this.state.hour === "12" || this.state.hour === "00") &&
      timeFormat.ampm === "am"
    ) ?
      this.setState({ hour: "12", minute: timeFormat.minute, ampm: timeFormat.ampm }) :
      this.setState({ hour: timeFormat.hour, minute: timeFormat.minute, ampm: timeFormat.ampm })
  }

  getTimeFromDateObj(time: Date) {
    const momentTime = moment(time)
    const simpleTime = { Hour: momentTime.get("hour"), Minute: momentTime.get("minute") }
    this.getTimeFromSimpleTime(simpleTime)
    this.props.handleChange &&
      this.props.handleChange(simpleTime)
    this.handleSlowChange(simpleTime)
  }

  hourKeyPress(e) {
    if (this.props.checkAllowChange && !this.props.checkAllowChange()) {
      return
    }

    if (e.which === 13) {
      e.preventDefault()
      this.minuteField.focus()
      return
    }

    let char = String.fromCharCode(e.which).toLowerCase().replace(/\D/g, "")

    if (char.length > 0) {
      const num = parseInt(char, 10)

      if (this.state.hourBuffer[0] == null || (this.state.hourBuffer[0] && this.state.hourBuffer[1])) {
        this.state.hourBuffer[1] = null
        // if number is 2-9. If we are going to handle 24 hour time, then get rid  of am/pm. Make that a param.
        if (num > 1) {
          this.state.hourBuffer[0] = "0"
          this.state.hourBuffer[1] = char
        }
        else {
          this.state.hourBuffer[0] = char
        }
      }
      else if (this.state.hourBuffer[1] == null) {
        // Don't let time be larger than 12
        if (num > 2) {
          if (this.state.hourBuffer[0] !== "0") {
            char = "2"
          }
        }
        this.state.hourBuffer[1] = char
      }

      let hourString = (this.state.hourBuffer[0] ? this.state.hourBuffer[0] : "0") + (this.state.hourBuffer[1] ? this.state.hourBuffer[1] : "0")

      if (this.state.hourBuffer[0] === "1" && this.state.hourBuffer[1] == null) {
        hourString = "01"
      }

      if (this.state.hourBuffer[0] && this.state.hourBuffer[1]) {
        e.preventDefault()
        this.minuteField.focus()
      }
      else {
        this.hourField.focus()
      }

      this.setState({ hour: hourString }, () =>
        this.changeSimpleTime()
      )
    }
    else {
      this.hourField.focus()
    }
  }

  minuteKeyPress(e) {
    if (this.props.checkAllowChange && !this.props.checkAllowChange()) {
      return
    }

    if (e.which === 13) {
      e.preventDefault()
      this.ampmField.focus()
      return
    }

    const char = String.fromCharCode(e.which).toLowerCase().replace(/\D/g, "")

    if (char.length > 0) {
      const num = parseInt(char, 10)
      if (this.state.minuteBuffer[0] == null || (this.state.minuteBuffer[0] && this.state.minuteBuffer[1])) {
        this.state.minuteBuffer[1] = null
        // if number is 6-9
        if (num > 5) {
          this.state.minuteBuffer[0] = "0"
          this.state.minuteBuffer[1] = char
        }
        else {
          this.state.minuteBuffer[0] = char
        }
      }
      else if (this.state.minuteBuffer[1] == null) {
        this.state.minuteBuffer[1] = char
      }

      if (this.state.minuteBuffer[0] && this.state.minuteBuffer[1]) {
        e.preventDefault()
        this.ampmField.focus()
      }
      else {
        this.minuteField.focus()
      }

      this.setState({
        minute: (this.state.minuteBuffer[0] ? this.state.minuteBuffer[0] : "0") + (this.state.minuteBuffer[1] ? this.state.minuteBuffer[1] : "0")
      }, () =>
        this.changeSimpleTime()
      )
    }
    else {
      this.minuteField.focus()
    }
  }

  amPmKeyPress(e) {
    if (this.props.checkAllowChange && !this.props.checkAllowChange()) {
      return
    }

    if (e.which === 13) {
      this.ampmField.input.blur()
      return
    }

    let keynum = e.which

    if (String.fromCharCode(keynum).toLowerCase() === "p") {
      this.setState({ ampm: "pm" }, () => this.changeSimpleTime())
    }
    else if (String.fromCharCode(keynum).toLowerCase() === "a") {
      this.setState({ ampm: "am" }, () => this.changeSimpleTime())
    }
  }

  changeSimpleTime() {
    if (this.props.handleChange) {
      const simpleTime = timeHelper.getSimpleTimeFromHourMinuteAm(this.state.hour, this.state.minute, this.state.ampm)
      this.props.handleChange(simpleTime)
    }
    else {
      this.setState({ dirty: true })
    }
  }

  onBlur() {
    if (isMobile) return
    if (this.state.dirty) {
      this.saveTimeout &&
        clearTimeout(this.saveTimeout)

      //Check for focus after timeout to let onBlue event finish
      this.saveTimeout = setTimeout(() => {
        if (!(this.hourField.state.isFocused || this.minuteField.state.isFocused || this.ampmField.state.isFocused)) {
          this.setState({ dirty: false })
          const simpleTime = timeHelper.getSimpleTimeFromHourMinuteAm(this.state.hour, this.state.minute, this.state.ampm)
          this.handleSlowChange(simpleTime)
        }
      }, 200)
    }
  }

  handleSlowChange(simpleTime: ISimpleTime) {
    this.props.handleSlowChange &&
      this.props.handleSlowChange(simpleTime, () => this.getTimeFromSimpleTime(this.props.simpleTime))
  }

  render() {
    const simpleTime = timeHelper.getSimpleTimeFromHourMinuteAm(this.state.hour, this.state.minute, this.state.ampm)
    return (
      <div style={{ position: "relative" }}>
        {this.props.label &&
          <div
            className="TextOverflow"
            title={this.props.label}
            style={{ position: "absolute", top: -10, fontSize: 12, lineHeight: "22px", color: "rgba(0, 0, 0, 0.3)", width: "100%" }}
          >
            {this.props.label}
          </div>
        }
        <div style={{ marginTop: this.props.label && 25 }}>
          {isMobile ?
            !this.props.handleMobileClick ?
              <TimePicker
                name="MobileSimpleTime"
                value={moment().hour(simpleTime.Hour).minute(simpleTime.Minute).toDate()}
                onChange={(e, time) => this.getTimeFromDateObj(time)}
                textFieldStyle={{ width: 70, ...this.props.textFieldStyle }}
                disabled={this.props.readonly}
              /> :
              <div style={{ display: "flex", alignItems: "center" }}>
                <MobileClickableTextField
                  data-intercom-target={`${this.props.dataIntercomTargetStr}Hours`}
                  key="hour"
                  style={{ width: this.props.smallText ? 15 : 20, fontSize: this.props.smallText && 12 }}
                  textStyle={this.props.textFieldStyle}
                  text={this.state.hour}
                  onClick={() => this.props.handleMobileClick()}
                />
                <span key="quote" style={{ color: this.props.semicolonColor && this.props.semicolonColor, display: "flex", alignItems: "center" }}>:</span>
                <MobileClickableTextField
                  data-intercom-target={`${this.props.dataIntercomTargetStr}Mins`}
                  key="minute"
                  style={{ width: this.props.smallText ? 15 : 20, fontSize: this.props.smallText && 12 }}
                  textStyle={this.props.textFieldStyle}
                  text={this.state.minute}
                  onClick={() => this.props.handleMobileClick()}
                />
                <MobileClickableTextField
                  data-intercom-target={`${this.props.dataIntercomTargetStr}AmPm`}
                  key="ampm"
                  style={{ width: this.props.smallText ? 18 : 25, fontSize: this.props.smallText && 12 }}
                  textStyle={this.props.textFieldStyle}
                  text={this.state.ampm}
                  onClick={() => this.props.handleMobileClick()}
                />
              </div> :
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                data-intercom-target={this.props.dataIntercomTargetStr ? `${this.props.dataIntercomTargetStr}-Hours` : "Select-Time-Entry-Hour"}
                name="hourField"
                value={this.state.hour}
                style={{ width: this.props.smallText ? 15 : 20, fontSize: this.props.smallText && 12 }}
                inputStyle={this.props.textFieldStyle}
                onBlur={this.onBlur}
                onKeyPress={this.hourKeyPress}
                ref={item => this.hourField = item}
                disabled={this.props.readonly}
              />
              <span
                style={this.props.smallText ?
                  { display: "flex", alignItems: "center", color: this.props.semicolonColor && this.props.semicolonColor } :
                  { fontWeight: "bolder", marginLeft: 2, marginRight: 4, color: this.props.semicolonColor && this.props.semicolonColor }
                }
              >:</span>
              <TextField
                data-intercom-target={this.props.dataIntercomTargetStr ? `${this.props.dataIntercomTargetStr}Mins` : "Select-Time-Entry-Min"}
                name="minuteField"
                value={this.state.minute}
                style={{ width: this.props.smallText ? 15 : 20, fontSize: this.props.smallText && 12 }}
                inputStyle={this.props.textFieldStyle}
                onBlur={this.onBlur}
                onKeyPress={this.minuteKeyPress}
                ref={item => this.minuteField = item}
                disabled={this.props.readonly}
              />
              <TextField
                data-intercom-target={this.props.dataIntercomTargetStr ? `${this.props.dataIntercomTargetStr}AmPm` : "Select-Time-Entry-AmPm"}
                name="ampmField"
                value={this.state.ampm}
                style={{ width: this.props.smallText ? 18 : 25, fontSize: this.props.smallText && 12, marginLeft: !this.props.smallText && 6 }}
                inputStyle={this.props.textFieldStyle}
                onKeyPress={this.amPmKeyPress}
                onBlur={this.onBlur}
                ref={item => this.ampmField = item}
                disabled={this.props.readonly}
              />
            </div>
          }
        </div>
      </div>
    )
  }
}